import React, { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import './OrientationLock.css';
import { isPhysicalMobileGameViewport } from './utils/isPhysicalMobileGameViewport';

function isElectronClient() {
  return (
    typeof window !== 'undefined' &&
    (window.location.protocol === 'file:' || window.electron !== undefined)
  );
}

function isCapacitorNativeClient() {
  try {
    return Capacitor.isNativePlatform() === true;
  } catch {
    return false;
  }
}

/** Usernames allowed to use the full game in a mobile web browser (case-insensitive). */
const WEB_MOBILE_ALLOWED_USERNAMES = new Set(['player1']);

export function isWebMobileAllowedUsername(username) {
  if (!username || typeof username !== 'string') return false;
  return WEB_MOBILE_ALLOWED_USERNAMES.has(username.trim().toLowerCase());
}

function shouldBlockWebMobile(allowWebMobile) {
  if (isElectronClient()) return false;
  if (isCapacitorNativeClient()) return false;
  if (!isPhysicalMobileGameViewport()) return false;
  if (allowWebMobile) return false;
  return true;
}

/**
 * Web: blocks handheld/touch small viewports (mobile browser not supported).
 * Capacitor Android/iOS and Electron are never blocked here.
 * allowWebMobile: when true (QA allow-list username e.g. player1 only), skip the block.
 * Otherwise shows the desktop-required screen before login/register.
 * When not blocked: locks to landscape and shows rotate prompt in portrait.
 */
function OrientationLock({ children, allowWebMobile = false }) {
  const [isPortrait, setIsPortrait] = useState(false);
  const [mobileBlocked, setMobileBlocked] = useState(() =>
    shouldBlockWebMobile(allowWebMobile)
  );

  useEffect(() => {
    const check = () => {
      setMobileBlocked(shouldBlockWebMobile(allowWebMobile));
      const isPortraitMode = window.innerWidth < window.innerHeight;
      setIsPortrait(isPortraitMode);
    };

    check();

    window.addEventListener('resize', check);
    window.addEventListener('orientationchange', check);

    if (screen.orientation && screen.orientation.lock) {
      screen.orientation.lock('landscape').catch(() => {});
    }

    return () => {
      window.removeEventListener('resize', check);
      window.removeEventListener('orientationchange', check);
      document.body.classList.remove('orientation-locked');
    };
  }, [allowWebMobile]);

  useEffect(() => {
    if (mobileBlocked || isPortrait) {
      document.body.classList.add('orientation-locked');
    } else {
      document.body.classList.remove('orientation-locked');
    }
    return () => {
      document.body.classList.remove('orientation-locked');
    };
  }, [mobileBlocked, isPortrait]);

  if (mobileBlocked) {
    return (
      <div className="orientation-lock-message">
        <div className="orientation-lock-content">
          <h2>Desktop required</h2>
          <p>CODA is not supported on phones and tablets yet.</p>
          <p>Please open this link on a desktop or laptop browser to play or create an account.</p>
        </div>
      </div>
    );
  }

  if (isPortrait) {
    return (
      <div className="orientation-lock-message">
        <div className="orientation-lock-content">
          <h2>Please Rotate Your Device</h2>
          <p>This game requires landscape orientation.</p>
          <p>Please rotate your device to continue.</p>
          <div className="orientation-lock-icon">📱</div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

export default OrientationLock;
